import { createPopper, type Options } from "@popperjs/core";
import type { Action } from "svelte/action";

export type TooltipProps = {
  title: string;
  options?: Partial<Options>;
};

/**
 * A Svelte action that adds Popper.js tooltips to elements.
 *
 * ```svelte
 * <script lang="ts">
 *   import tooltip from "$lib/tooltip";
 * </script>
 *
 * <button use:tooltip={"Tooltip text"}>
 *   Hover me!
 * </button>
 * ```
 *
 * For more information, see the Popper.js v2 tutorial:
 * https://popper.js.org/docs/v2/tutorial/
 */
const tooltip: Action<HTMLElement, string | TooltipProps> = (node, param) => {
  const props: TooltipProps =
    typeof param === "object" ? param : { title: param ?? "" };

  const tooltip = document.createElement("div");
  tooltip.className = "popper-tooltip";
  tooltip.style.display = props.title ? "block" : "none";
  const textNode = document.createTextNode(props.title);
  tooltip.appendChild(textNode);

  const arrow = document.createElement("div");
  arrow.className = "popper-arrow";
  arrow.setAttribute("data-popper-arrow", "");
  tooltip.appendChild(arrow);

  node.parentElement?.insertBefore(tooltip, node.nextSibling);
  const popperInstance = createPopper(node, tooltip);

  let active = false;
  let options = props.options;

  const providedModifiers = options?.modifiers ?? [];

  function updateOptions() {
    popperInstance.setOptions({
      ...options,
      modifiers: [
        { name: "offset", options: { offset: [0, 12] } },
        { name: "eventListeners", enabled: active },
        { name: "arrow", options: { padding: 8 } },
        { name: "preventOverflow", options: { padding: 16 } },
        ...providedModifiers,
      ],
    });
  }

  updateOptions();

  function show() {
    active = true;
    updateOptions();
    tooltip.setAttribute("data-show", "");

    // We need to tell Popper to update the tooltip position
    // after we show the tooltip, otherwise it will be incorrect
    popperInstance.update();
  }

  function hide() {
    active = false;
    updateOptions();
    tooltip.removeAttribute("data-show");
  }

  const showEvents = ["mouseenter", "focus"];
  const hideEvents = ["mouseleave", "blur"];

  showEvents.forEach((event) => {
    node.addEventListener(event, show);
  });

  hideEvents.forEach((event) => {
    node.addEventListener(event, hide);
  });

  return {
    update(param) {
      const props: TooltipProps =
        typeof param === "object" ? param : { title: param ?? "" };
      let changed = false;
      if (textNode.data !== props.title) {
        textNode.data = props.title;
        tooltip.style.display = props.title ? "block" : "none";
        changed = true;
      }
      if (options !== props.options) {
        options = props.options;
        updateOptions();
        changed = true;
      }
      if (changed) {
        popperInstance.update();
      }
    },

    destroy() {
      showEvents.forEach((event) => {
        node.removeEventListener(event, show);
      });

      hideEvents.forEach((event) => {
        node.removeEventListener(event, hide);
      });
    },
  };
};

export default tooltip;
